import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LuInbox } from 'react-icons/lu';
import { AgGridReact } from 'ag-grid-react';
import { useIsMobile, useIsTouch } from 'hooks/useBreakpoints';
import MobileCardList from './ag-grid/MobileCardList';
import { pinFirstDataColumn } from './ag-grid/pinFirstDataColumn';
import agGridTheme from './agGridTheme';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
// AllEnterpriseModule is needed for master/detail (expandable detail rows). The
// enterprise licence is already set app-wide (App.js) and other grids register
// it too — registration is a global singleton, so this is safe/idempotent.
import { AllEnterpriseModule } from 'ag-grid-enterprise';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

// Empty state rendered inside AG Grid's no-rows overlay — one professional
// look for every grid instead of the stock "No Rows To Show" text. Kept
// compact because grids often sit in dense layouts.
const GridNoRowsOverlay = () => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-py-8">
    <LuInbox size={32} className="tw-text-slate-300" />
    <span className="tw-text-sm tw-font-medium tw-text-slate-500">No data</span>
    <span className="tw-text-xs tw-text-slate-400">
      Try adjusting the filters, or add a new record.
    </span>
  </div>
);

// Synthetic columns prepended by AgGridTable (module-level so their identity is stable).
const CHECKBOX_COL_DEF = {
  headerName: '',
  width: 40, minWidth: 40, flex: 0,
  pinned: 'left',
  checkboxSelection: true, headerCheckboxSelection: true,
  sortable: false, filter: false, resizable: false, suppressMovable: true,
  cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
};

const ROW_NUMBER_COL_DEF = {
  headerName: 'No.',
  valueGetter: (params) => params.node.rowIndex + 1,
  width: 55,
  minWidth: 55,
  flex: 0,
  sortable: false,
  filter: false,
  resizable: false,
  cellStyle: { color: '#94a3b8', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};

/**
 * AgGridTable — Tailwind-styled AG Grid wrapper for use in taiwincss-components.
 * Direct import of ag-grid-react (no dynamic loading fallback).
 * Supports server-side pagination, multi-select, grouping, fixed/auto height.
 */
const AgGridTable = ({
  // Core
  rowData = [],
  columnDefs = [],

  // Operations
  serverSide = false,
  onFetch = null,
  loading = false,
  totalRecords = 0,
  pageSize = 20,

  // Features
  filter = null,   // null = auto: on only when column filters round-trip to the
                   // server (serverSide + onFetch). Pass true/false to override.
  sort = true,
  pagination = true,
  grouping = false,
  groupBy = null,
  expandChildren = false,
  multiSelect = false,

  // Master/detail (Enterprise): expandable detail row per master row.
  masterDetail = false,
  detailCellRenderer = null,
  detailCellRendererParams = null,
  isRowMaster = null,
  detailRowHeight = null,
  detailRowAutoHeight = false,

  // Tree data (Enterprise): one flat dataset with a hierarchy path per row.
  treeData = false,
  getDataPath = null,
  autoGroupColumnDef = null,
  groupDefaultExpanded = null,

  // Styling
  rowHeight = 35,
  headerHeight = 45,
  height = null,      // Fixed height string e.g. '65vh'. null = autoHeight
  minHeight = '400px', // autoHeight only: floor of the grid area (null = just the rows)
  fontSize = '13px',
  className = '',
  noRowsOverlayComponent = GridNoRowsOverlay,

  // Pinned rows
  pinnedBottomRowData = null,
  pinnedTopRowData = null,

  // Row styling
  getRowStyle = null,

  // Callbacks
  onRowClick = null,
  onSelectionChanged = null,
  onGridReady = null,
  onSortChanged = null,

  // Flags
  enableCellTextSelection = false,
  rowNumbers = false,
  suppressRowTransform = false,

  // Phone rendering. Omit it and the grid behaves exactly as before — the
  // heavy report grids (40-52 columns) are meant to stay a grid, because a card
  // of fifty fields is worse to read than a table you scroll.
  //   mobileCard={{ title: 'j_number', subtitle: 'name', meta: ['status'] }}
  mobileCard = null,

  // Column default overrides (merged, takes priority over built-in defaultColDef)
  colDefOverride = {},
}) => {
  const isMobile = useIsMobile();
  const isTouch  = useIsTouch();

  const [filterModel, setFilterModel] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [visKey, setVisKey] = useState(0);
  const gridRef = useRef(null);
  const themeWrapperRef = useRef(null);

  // AG Grid lays out against its container size. When the grid is rendered inside
  // a tab/accordion toggled with display:none (kept mounted, just hidden), it
  // initialises at 0×0 and, once shown again, paints nothing (empty) or a stale
  // overlay even though its rowData is intact — and a redraw doesn't reliably
  // recover it. Watch the wrapper: each time it transitions hidden → visible,
  // bump a key so AG Grid remounts fresh against the now-correct size and paints
  // the current rowData. State reset (sort/scroll) on re-show is an acceptable
  // trade for never showing an empty/stuck grid.
  useEffect(() => {
    const el = themeWrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    // offsetParent is null while any ancestor is display:none.
    let wasVisible = el.offsetParent !== null && el.clientHeight > 0;
    const ro = new ResizeObserver(() => {
      const visible = el.offsetParent !== null && el.clientHeight > 0;
      if (visible && !wasVisible) setVisKey((k) => k + 1);
      wasVisible = visible;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const triggerServerFetch = useCallback(({ page, pSize, sort, filter }) => {
    if (onFetch) {
      onFetch({
        page,
        pageSize: pSize,
        sortBy: sort[0]?.colId,
        sortDir: sort[0]?.sort === 'asc' ? 'asc' : 'desc',
        filters: filter,
        groupBy,
      });
    }
  }, [onFetch, groupBy]);

  const handleFilterChanged = useCallback((event) => {
    if (!serverSide || !onFetch) return;
    const filters = event.api.getFilterModel();
    setFilterModel(filters);
    setCurrentPage(1);
    triggerServerFetch({ page: 1, pSize: pageSize, sort: sortModel, filter: filters });
  }, [serverSide, onFetch, pageSize, sortModel, triggerServerFetch]);

  const handleSortChanged = useCallback((event) => {
    const model = event.api.getSortModel ? event.api.getSortModel() : [];
    setSortModel(model);
    if (onSortChanged) {
      onSortChanged(model.length > 0 ? model[0] : { colId: null, sort: null });
    }
    if (!serverSide || !onFetch) return;
    setCurrentPage(1);
    triggerServerFetch({ page: 1, pSize: pageSize, sort: model, filter: filterModel });
  }, [serverSide, onFetch, pageSize, filterModel, triggerServerFetch, onSortChanged]);

  const handlePaginationChanged = useCallback((event) => {
    if (!serverSide || !onFetch) return;
    // newData=true means rowData changed (not a user page click) — skip to avoid fetch loops
    if (event.newData) return;
    if (!event.newPage) return;
    const page = event.api.paginationGetCurrentPage() + 1;
    setCurrentPage(page);
    triggerServerFetch({ page, pSize: pageSize, sort: sortModel, filter: filterModel });
  }, [serverSide, onFetch, pageSize, sortModel, filterModel, triggerServerFetch]);

  // Memoized so column identity is stable across data-only re-renders. A new
  // array/object each render makes AG Grid recreate its columns constantly,
  // which — with tree-data's auto group column — can fire the column-animation
  // path before the grid body is ready and crash (setColumnMovingCss on undefined).
  const finalColumnDefs = useMemo(() => {
    const synthetic = (multiSelect ? 1 : 0) + (rowNumbers ? 1 : 0);
    const cols = [
      ...(multiSelect ? [CHECKBOX_COL_DEF] : []),
      ...(rowNumbers  ? [ROW_NUMBER_COL_DEF] : []),
      ...columnDefs,
    ];
    return isMobile ? pinFirstDataColumn(cols, synthetic) : cols;
  }, [columnDefs, multiSelect, rowNumbers, isMobile]);

  // Column filters are only trustworthy when the grid either holds the full
  // dataset or refetches from the server on every filter change (serverSide +
  // onFetch). With externally-paginated data (serverSide, no onFetch) the grid
  // holds just the current page, so a column filter silently filters within
  // that page — e.g. a set filter listing 3 of 13,000 PCV numbers. Auto mode
  // (filter=null) therefore only enables filters for the onFetch pattern;
  // grids that legitimately need them elsewhere opt in with filter={true}.
  const filterEnabled = filter != null ? filter : (serverSide && !!onFetch);

  const defaultColDef = useMemo(() => ({
    filter: filterEnabled,
    sortable: sort,
    resizable: true,
    flex: 1,
    minWidth: 100,
    ...colDefOverride,
  }), [filterEnabled, sort, colDefOverride]);

  // True server-side paging: when the parent drives paging itself (serverSide with
  // its own external <Pagination>, i.e. no onFetch), AG Grid must NOT paginate the
  // current server page on top of it — otherwise you get a second, client-side
  // pager over the already-paged rows (e.g. "20/page · 3 pages" atop "1–50 of N").
  // Grids that pass onFetch let AG Grid's pager drive server fetches, so keep it.
  const paginationEnabled = serverSide && !onFetch ? false : pagination;

  if (isMobile && mobileCard) {
    return (
      <div className={`tw-w-full tw-flex tw-flex-col tw-gap-2 ${className}`}>
        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-text-xs tw-text-blue-700">
            <div className="tw-animate-spin tw-rounded-xl tw-h-3.5 tw-w-3.5 tw-border-t-2 tw-border-b-2 tw-border-blue-500" />
            Loading…
          </div>
        )}
        <MobileCardList
          rowData={rowData}
          columnDefs={columnDefs}
          config={mobileCard}
          onRowClick={onRowClick}
          pageSize={pageSize}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div className={`tw-w-full tw-flex tw-flex-col ${className}`}>
      {/* Suppress AG Grid's built-in "Loading..." overlay. AG Grid shows it while
          a grid initialises and re-shows it when the grid's container flips from
          display:none back to visible (tab switch / accordion expand) — which
          left a stale "Loading..." box on grids whose data was already present.
          This component surfaces real async loading via the `loading` bar above,
          so the native overlay is redundant. The "No rows" overlay is untouched.
          Scoped to our own wrapper class (theming is class-less in v34). */}
      <style>{`.ptl-grid .ag-overlay-loading-wrapper{ display:none !important; }`}</style>

      {/* Loading indicator */}
      {loading && (
        <div className="tw-flex tw-items-center tw-justify-center tw-p-3 tw-bg-blue-50 tw-border-b tw-border-blue-200">
          <div className="tw-animate-spin tw-rounded-xl tw-h-4 tw-w-4 tw-border-t-2 tw-border-b-2 tw-border-blue-500 tw-mr-2" />
          <span className="tw-text-sm tw-text-blue-700">Loading...</span>
        </div>
      )}

      {/* AG Grid */}
      <div
        ref={themeWrapperRef}
        className="ptl-grid"
        style={{
          ...(height ? { height } : (minHeight ? { minHeight } : {})),
          '--ag-font-size': fontSize,
        }}
      >
        <AgGridReact
          key={visKey}
          ref={gridRef}
          theme={agGridTheme}
          rowData={rowData}
          columnDefs={finalColumnDefs}
          defaultColDef={defaultColDef}
          noRowsOverlayComponent={noRowsOverlayComponent}

          // Pagination
          pagination={paginationEnabled}
          paginationPageSize={pageSize}
          // The page-size select pushes AG Grid's pager wider than a phone.
          paginationPageSizeSelector={isMobile ? false : [10, 20, 50, 100]}
          suppressPaginationPanel={!paginationEnabled}

          // Selection
          rowSelection={multiSelect ? 'multiple' : 'single'}
          suppressRowClickSelection={multiSelect}

          // Display
          // 35px rows are below the 44px minimum tap target, and a mistap in a
          // grid usually means opening the wrong record. Keyed to pointer type,
          // not width: a tablet at 1024px is still a finger, a narrow desktop
          // window is still a mouse.
          rowHeight={isTouch ? Math.max(rowHeight, 44) : rowHeight}
          headerHeight={headerHeight}
          headerGroupHeight={45}
          enableCellTextSelection={enableCellTextSelection}
          domLayout={height ? undefined : 'autoHeight'}

          // Server-side
          {...(serverSide && {
            serverSidePagination: true,
            serverSideFilteringAlwaysResets: true,
            cacheBlockSize: pageSize,
          })}

          // Grouping (Enterprise)
          groupDisplayType={grouping ? 'groupRows' : undefined}
          autoGroupColumnDef={
            treeData
              ? autoGroupColumnDef
              : (grouping ? { minWidth: 300, cellRenderer: 'agGroupCellRenderer' } : undefined)
          }
          groupDefaultExpanded={groupDefaultExpanded != null ? groupDefaultExpanded : (grouping ? -1 : 0)}

          // Tree data (Enterprise)
          {...(treeData && {
            treeData: true,
            ...(getDataPath ? { getDataPath } : {}),
            // Auto group column + column-move animation race the grid-body init
            // and can crash (setColumnMovingCss on undefined) — the animation is
            // purely cosmetic here, so turn it off for tree data.
            suppressColumnMoveAnimation: true,
          })}

          // Expand children (Enterprise)
          {...(expandChildren && grouping && {
            groupIncludeFooter: true,
            groupSelectsChildren: true,
          })}

          // Master/detail (Enterprise). Omit detailCellRenderer when not given so
          // AG Grid uses its native detail grid (via detailCellRendererParams
          // .detailGridOptions) — one integrated grid instead of a nested wrapper.
          {...(masterDetail && {
            masterDetail: true,
            ...(detailCellRenderer ? { detailCellRenderer } : {}),
            ...(detailCellRendererParams ? { detailCellRendererParams } : {}),
            ...(isRowMaster && { isRowMaster }),
            ...(detailRowAutoHeight ? { detailRowAutoHeight: true } : {}),
            ...(detailRowHeight ? { detailRowHeight } : {}),
          })}

          // Row spanning
          suppressRowTransform={suppressRowTransform}

          // Pinned rows
          {...(pinnedBottomRowData != null && { pinnedBottomRowData })}
          {...(pinnedTopRowData    != null && { pinnedTopRowData    })}

          // Row styling
          {...(getRowStyle != null && { getRowStyle })}

          // Events
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          onPaginationChanged={handlePaginationChanged}
          onRowClicked={onRowClick}
          onSelectionChanged={(event) => {
            const selected = event.api.getSelectedRows();
            setSelectedRows(selected);
            onSelectionChanged?.(selected);
          }}
          onGridReady={onGridReady}
        />
      </div>

      {/* Footer info — only for the onFetch-driven pattern; external-pagination
          grids show their own <Pagination>, so this would be a redundant/stale line. */}
      {serverSide && onFetch && rowData.length > 0 && (
        <div className="tw-px-4 tw-py-2 tw-bg-slate-50 tw-border-t tw-border-slate-200 tw-text-xs tw-text-slate-500 tw-flex tw-justify-between">
          <span>
            {`Page ${currentPage} of ~${Math.ceil(totalRecords / pageSize)}`}
          </span>
          {selectedRows.length > 0 && (
            <span className="tw-font-medium tw-text-primary-600">
              {selectedRows.length} selected
            </span>
          )}
        </div>
      )}
    </div>
  );
};

AgGridTable.propTypes = {
  rowData: PropTypes.array,
  columnDefs: PropTypes.array,
  serverSide: PropTypes.bool,
  onFetch: PropTypes.func,
  loading: PropTypes.bool,
  noRowsOverlayComponent: PropTypes.elementType,
  totalRecords: PropTypes.number,
  pageSize: PropTypes.number,
  filter: PropTypes.bool,
  sort: PropTypes.bool,
  pagination: PropTypes.bool,
  grouping: PropTypes.bool,
  groupBy: PropTypes.string,
  expandChildren: PropTypes.bool,
  multiSelect: PropTypes.bool,
  masterDetail: PropTypes.bool,
  detailCellRenderer: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  detailCellRendererParams: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  isRowMaster: PropTypes.func,
  detailRowHeight: PropTypes.number,
  detailRowAutoHeight: PropTypes.bool,
  treeData: PropTypes.bool,
  getDataPath: PropTypes.func,
  autoGroupColumnDef: PropTypes.object,
  groupDefaultExpanded: PropTypes.number,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  fontSize: PropTypes.string,
  className: PropTypes.string,
  pinnedBottomRowData: PropTypes.array,
  pinnedTopRowData:    PropTypes.array,
  getRowStyle:         PropTypes.func,
  onRowClick:          PropTypes.func,
  onSelectionChanged:  PropTypes.func,
  onGridReady:         PropTypes.func,
  onSortChanged:       PropTypes.func,
  enableCellTextSelection: PropTypes.bool,
  suppressRowTransform:    PropTypes.bool,
  rowNumbers:          PropTypes.bool,
  colDefOverride:      PropTypes.object,
  mobileCard: PropTypes.shape({
    title:          PropTypes.string,
    subtitle:       PropTypes.string,
    meta:           PropTypes.arrayOf(PropTypes.string),
    renderTitle:    PropTypes.func,
    renderSubtitle: PropTypes.func,
    renderBadge:    PropTypes.func,
  }),
};

export default AgGridTable;
